import useGetBonhamImage from '../../../services/queries/useGetBonhamImage'
import usePostBulkUploadImages from '../../../services/queries/usePostBulkUploadImages'
import { refreshAuthToken, setupTokenRefresh } from './tokenRefresh'

/**
 * Processes an image blob to resize to 246px height (maintaining aspect ratio) and pad to 624px width with white
 * Returns a File object ready for upload
 */
const processImageToWalletSize = async (blob: Blob, fileName: string): Promise<File> => {
  return new Promise((resolve, reject) => {
    const img = new Image()
    const url = URL.createObjectURL(blob)

    img.onload = () => {
      try {
        // Target dimensions
        const targetWidth = 624
        const targetHeight = 246

        // Aspect ratio of source image
        const ar = img.width / img.height

        // Start with height-fit: scale to 246px tall
        let scaledHeight = targetHeight
        let scaledWidth = Math.round(targetHeight * ar)

        // Prevent cropping: if too wide, fall back to width-fit
        if (scaledWidth > targetWidth) {
          scaledWidth = targetWidth
          scaledHeight = Math.round(targetWidth / ar)
        }

        // Center in both directions, with white padding around
        const xOffset = Math.round((targetWidth - scaledWidth) / 2)
        const yOffset = Math.round((targetHeight - scaledHeight) / 2)

        // Create canvas - this is just the output container (624x246)
        const canvas = document.createElement('canvas')
        canvas.width = targetWidth
        canvas.height = targetHeight
        const ctx = canvas.getContext('2d')

        if (!ctx) {
          reject(new Error('Failed to get canvas context'))
          return
        }

        // Fill with white background
        ctx.fillStyle = '#FFFFFF'
        ctx.fillRect(0, 0, targetWidth, targetHeight)

        // Draw scaled image centered, with no cropping
        ctx.drawImage(img, xOffset, yOffset, scaledWidth, scaledHeight)

        // Convert canvas to blob - always use image/jpeg MIME type
        canvas.toBlob(
          processedBlob => {
            URL.revokeObjectURL(url)
            if (!processedBlob) {
              reject(new Error('Failed to process image'))
              return
            }

            // Ensure filename has .jpg extension for proper MIME type
            const sanitizedFileName = `${fileName.replace(/\.[^.]+$/, '')}.jpg`

            // Create a new Blob with explicit image/jpeg type to ensure MIME type is correct
            const jpegBlob = new Blob([processedBlob], { type: 'image/jpeg' })

            // Always use image/jpeg MIME type for upload
            const processedFile = new File([jpegBlob], sanitizedFileName, { type: 'image/jpeg' })

            if (process.env.NODE_ENV === 'development') {
              console.log(
                `Processed file: ${sanitizedFileName}, type: ${processedFile.type}, size: ${processedFile.size}`
              )
            }

            resolve(processedFile)
          },
          'image/jpeg', // Always use JPEG format
          0.95 // Quality for JPEG
        )
      } catch (error) {
        URL.revokeObjectURL(url)
        reject(error)
      }
    }

    img.onerror = () => {
      URL.revokeObjectURL(url)
      reject(new Error('Failed to load image'))
    }

    img.src = url
  })
}

/**
 * Normalizes filename for matching (removes extension, converts to lowercase)
 */
const normalizeFileName = (fileName: string): string => {
  return fileName.toLowerCase().replace(/\.[^/.]+$/, '')
}

/**
 * Processes images from GCP bucket based on CSV Product Image column
 * Only downloads images that are specified in the CSV
 * Returns a map of row index to uploaded image URL
 */
export const processGcpImages = async (
  csvData: Array<{ 'Product Image'?: string; [key: string]: string | undefined }>,
  brandId: string,
  bulkUploadImagesMutation: ReturnType<typeof usePostBulkUploadImages>,
  getBonhamImageMutation: ReturnType<typeof useGetBonhamImage>,
  onProgress?: (current: number, total: number, status: string) => void
): Promise<Map<number, { appleImageUrl: string; googleImageUrl: string }>> => {
  const imageMap = new Map<number, { appleImageUrl: string; googleImageUrl: string }>()

  try {
    // Extract unique image filenames from CSV Product Image column
    const imageFileMap = new Map<string, Array<{ rowIndex: number; fileName: string }>>()

    csvData.forEach((row, index) => {
      const productImage = row['Product Image'] || ''
      if (!productImage) return

      // Extract filename preserving original case (GCS is case-sensitive)
      const firstPart = productImage.split(',')[0].trim()
      const withoutQuery = firstPart.split('?')[0].split('#')[0]
      const originalFileName = withoutQuery.split('/').pop() || withoutQuery

      if (!originalFileName) return

      // Use lowercase for matching purposes
      const normalizedFileName = normalizeFileName(originalFileName)

      // Store only the filename (not the path) - backend will handle the path
      if (!imageFileMap.has(originalFileName)) {
        imageFileMap.set(originalFileName, [])
      }
      imageFileMap.get(originalFileName)!.push({ rowIndex: index, fileName: normalizedFileName })
    })

    const uniqueImageFilenames = Array.from(imageFileMap.keys())

    if (uniqueImageFilenames.length === 0) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('No images found in CSV Product Image column')
      }
      return imageMap
    }

    if (process.env.NODE_ENV === 'development') {
      console.log(`Found ${uniqueImageFilenames.length} unique image(s) to download from CSV`)
    }

    // Set up token refresh
    const cleanupTokenRefresh = setupTokenRefresh()

    try {
      // Download and upload images in batches of 100
      const DOWNLOAD_BATCH_SIZE = 100
      const uploadCounter = { count: 0 }
      const totalImages = uniqueImageFilenames.length
      const totalRowsNeedingImages = csvData.filter(row => row['Product Image']).length

      // eslint-disable-next-line no-await-in-loop
      for (let i = 0; i < uniqueImageFilenames.length; i += DOWNLOAD_BATCH_SIZE) {
        const batch = uniqueImageFilenames.slice(i, i + DOWNLOAD_BATCH_SIZE)
        onProgress?.(
          i,
          totalImages,
          `Downloading and uploading images ${i + 1}-${Math.min(
            i + DOWNLOAD_BATCH_SIZE,
            totalImages
          )}/${totalImages}...`
        )

        // Refresh token periodically
        if (i > 0 && i % 100 === 0) {
          // eslint-disable-next-line no-await-in-loop
          await refreshAuthToken()
        }

        // Download, process, and upload each image immediately
        const processAndUploadPromises = batch.map(async fileName => {
          try {
            // Download image using the hook
            const blob = await getBonhamImageMutation.mutateAsync({ imageName: fileName })

            // Get rows that need this image
            const rows = imageFileMap.get(fileName) || []
            if (rows.length === 0) return

            // Process image once (will be used for all rows that reference it)
            const normalizedFileName = normalizeFileName(fileName)
            const processedFile = await processImageToWalletSize(blob, normalizedFileName)

            // Upload immediately (both Apple and Google wallet versions)
            const [appleUploadResponse, googleUploadResponse] = await Promise.all([
              bulkUploadImagesMutation.mutateAsync({
                brandId,
                type: 'APPLE_WALLET_IMAGE',
                files: [processedFile],
              }),
              bulkUploadImagesMutation.mutateAsync({
                brandId,
                type: 'GOOGLE_WALLET_IMAGE',
                files: [processedFile],
              }),
            ])

            // Map uploaded URLs to all rows that reference this image
            const appleUpload = appleUploadResponse.successfulUploads[0]
            const googleUpload = googleUploadResponse.successfulUploads[0]

            if (appleUpload && googleUpload) {
              const appleImageUrl =
                appleUpload.path || appleUpload.imagePathCompressedAsJpeg || appleUpload.webpUrl || ''
              const googleImageUrl =
                googleUpload.path || googleUpload.imagePathCompressedAsJpeg || googleUpload.webpUrl || ''

              rows.forEach(({ rowIndex }) => {
                imageMap.set(rowIndex, { appleImageUrl, googleImageUrl })
                if (process.env.NODE_ENV === 'development') {
                  console.log(`Uploaded and mapped image ${fileName} to row ${rowIndex}`)
                }
              })

              uploadCounter.count += rows.length
              // Update progress after each successful upload (not just at batch end)
              // Progress is based on rows processed, not unique images
              onProgress?.(
                uploadCounter.count,
                totalRowsNeedingImages,
                `Uploaded and mapped ${uploadCounter.count}/${totalRowsNeedingImages} rows...`
              )

              return true
            }

            // Log which upload failed
            if (process.env.NODE_ENV === 'development') {
              console.error(`Upload failed for ${fileName}:`, {
                appleUpload: appleUploadResponse.successfulUploads.length,
                googleUpload: googleUploadResponse.successfulUploads.length,
                appleFailed: appleUploadResponse.failedUploads,
                googleFailed: googleUploadResponse.failedUploads,
              })
            }
            return false
          } catch (error) {
            if (process.env.NODE_ENV === 'development') {
              console.error(`Failed to download/upload ${fileName}:`, error)
            }
            return false
          }
        })

        // eslint-disable-next-line no-await-in-loop
        await Promise.all(processAndUploadPromises)
      }

      if (imageMap.size === 0) {
        throw new Error(
          `Failed to download and upload any images. Processed ${uniqueImageFilenames.length} unique images but none were successfully uploaded.`
        )
      }

      cleanupTokenRefresh()
      return imageMap
    } catch (error) {
      cleanupTokenRefresh()
      throw error
    }
  } catch (error) {
    console.error('Error processing GCP bucket images:', error)
    throw new Error(`Failed to process GCP bucket images: ${error instanceof Error ? error.message : 'Unknown error'}`)
  }
}
